import React, { useRef } from 'react';
import { IconSearch, IconX, IconChevronLeft, IconChevronRight } from '../icons';
import { Button, IconButton } from './Button';
import { HelpTip } from './HelpTip';
import { statusTint } from '../../lib/theme';

// Page chrome — the top of every desktop page, per
// `BBM Context for claude/page-headers.md` (Jaylen's Page Headers Showroom
// picks, 2026-09-15: 1C big title + one toolbar card, 2B square chips that
// fill with their colour, 3B stepper + presets, 4B dashed empty state).
//
//   <PageHeader title="Clients" summary="254 clients · 68 new leads" help={<>…</>}
//               actions={<Button leftIcon={<IconPlus/>}>New Client</Button>} />
//   <Toolbar>
//       <SearchInput value onChange placeholder />
//       <FilterChips items value onChange />
//       <ChoiceKeys … className="ml-auto" />          // sort, at the right
//   </Toolbar>
//
// Period pages:
//   <Toolbar>
//       <PeriodStepper label="Sep 1 – 14, 2026" onPrev onNext onToday />
//       <Segmented items=[…presets] size="sm" />
//   </Toolbar>
//
// Nothing here: <EmptyState title hint action />

export const pageWrapCls = 'p-4 md:p-6 space-y-5';

// ── Header block ─────────────────────────────────────────────────────────
// Big LEMON MILK title + help dot, a live summary line under it (counts,
// not prose), actions at the right (primary New button; secondary Export).
export function PageHeader({ title, summary, help, helpTitle, actions, className = '' }) {
    return (
        <div className={`flex flex-col sm:flex-row sm:items-start justify-between gap-3 ${className}`}>
            <div className="min-w-0">
                <div className="flex items-center gap-2.5">
                    <h1 className="text-2xl md:text-3xl font-bold font-lemonmilk text-slate-900 leading-tight">{title}</h1>
                    {help && <HelpTip title={helpTitle || 'How this page works'}>{help}</HelpTip>}
                </div>
                {summary && <p className="text-slate-500 text-sm mt-1">{summary}</p>}
            </div>
            {actions && <div className="flex items-center gap-2 flex-wrap flex-shrink-0">{actions}</div>}
        </div>
    );
}

// ── Toolbar card ─────────────────────────────────────────────────────────
// One bordered card under the title holding search, chips, sort, steppers.
// Children wrap; put `className="ml-auto"` on the right-hand control.
export const toolbarCls = 'bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-3 items-center';
export function Toolbar({ children, className = '' }) {
    return <div className={`${toolbarCls} ${className}`}>{children}</div>;
}

// ── Search ───────────────────────────────────────────────────────────────
// 40px, icon at the left, clear ✕ when there's text. Grows to fill the
// toolbar row (`flex-1`), min 200px.
export function SearchInput({ value, onChange, placeholder = 'Search…', className = '', autoFocus = false, ariaLabel, ...rest }) {
    const ref = useRef(null);
    return (
        <div className={`relative flex-1 min-w-[200px] ${className}`}>
            <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
                ref={ref}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoFocus={autoFocus}
                aria-label={ariaLabel || placeholder}
                className="w-full h-10 pl-9 pr-9 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400"
                {...rest}
            />
            {value && (
                <IconButton label="Clear search" size="sm" className="absolute right-1 top-1/2 -translate-y-1/2" onClick={() => { onChange(''); ref.current?.focus(); }}>
                    <IconX size={14} />
                </IconButton>
            )}
        </div>
    );
}

// ── Filter chips ─────────────────────────────────────────────────────────
// Square 32px chips. Item shape: { key, label, count?, color? }.
// With a colour: tinted at rest, SOLID in that colour when active (white
// text). Without one (the "All" chip): outlined at rest, slate-900 active.
// Counts are always shown when provided (dimmed after the label).
export function FilterChips({ items = [], value, onChange, ariaLabel = 'Filter', className = '', scroll = false }) {
    return (
        <div role="group" aria-label={ariaLabel} className={`flex gap-1.5 ${scroll ? 'flex-nowrap overflow-x-auto' : 'flex-wrap'} ${className}`} style={scroll ? { scrollbarWidth: 'none' } : undefined}>
            {items.map((it) => {
                const active = it.key === value;
                const colored = !!it.color;
                const style = colored
                    ? (active ? { background: it.color, color: '#fff', borderColor: it.color } : { background: statusTint(it.color, it.bg), color: it.color, borderColor: 'transparent' })
                    : undefined;
                const cls = colored
                    ? 'border'
                    : (active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50');
                return (
                    <button
                        key={it.key}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(it.key)}
                        className={`h-8 px-3 rounded-lg border text-xs font-bold whitespace-nowrap inline-flex items-center gap-1.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 ${cls}`}
                        style={style}
                    >
                        {it.label}
                        {it.count != null && <span className={`font-semibold ${active ? 'opacity-80' : 'opacity-60'}`}>{it.count}</span>}
                    </button>
                );
            })}
        </div>
    );
}

// ── Period stepper ───────────────────────────────────────────────────────
// ‹ Today › with a bold tabular label. `todayLabel` renames the middle
// button ("This week", "Current period"). Presets go next to it as a
// <Segmented size="sm">.
export function PeriodStepper({ label, sublabel, onPrev, onNext, onToday, todayLabel = 'Today', className = '' }) {
    return (
        <div className={`flex items-center gap-1.5 ${className}`}>
            <IconButton label="Previous" size="sm" onClick={onPrev} className="border border-slate-300"><IconChevronLeft size={16} /></IconButton>
            {onToday && <Button variant="secondary" size="sm" onClick={onToday}>{todayLabel}</Button>}
            <IconButton label="Next" size="sm" onClick={onNext} className="border border-slate-300"><IconChevronRight size={16} /></IconButton>
            <div className="ml-2 leading-tight">
                {sublabel && <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{sublabel}</div>}
                <div className="text-[15px] font-bold text-slate-900 tabular-nums whitespace-nowrap">{label}</div>
            </div>
        </div>
    );
}

// ── Empty state ──────────────────────────────────────────────────────────
// Dashed card: small faded icon, a heading, one sentence, and the next
// step as a button. Same card whether the list is truly empty or filters
// hide everything — pass a different `hint` / `action`.
export function EmptyState({ icon, title = 'Nothing here yet', hint, action, className = '' }) {
    return (
        <div className={`bg-white border border-dashed border-slate-300 rounded-xl px-4 py-8 flex flex-col items-center text-center gap-2 ${className}`}>
            {icon && <div className="text-slate-300 mb-1">{icon}</div>}
            <p className="text-[15px] font-bold text-slate-700">{title}</p>
            {hint && <p className="text-sm text-slate-500 max-w-[36ch]">{hint}</p>}
            {action && <div className="mt-2">{action}</div>}
        </div>
    );
}

export default { PageHeader, Toolbar, SearchInput, FilterChips, PeriodStepper, EmptyState, pageWrapCls, toolbarCls };
